import {
  listApplications,
  type NewApplicationListItem,
} from "api/applications";
import { listAssignmentLeads } from "api/assignment";
import { listFollowUps } from "api/followUp";
import { listLegalCertificateLeads } from "api/legalCertificate";
import { listOppositionLeads } from "api/opposition";
import { listRenewalLeads } from "api/renewal";
import { listSurveillanceLeads } from "api/surveillance";

/**
 * Module keys mirroring backend `WorkflowType`. Source of truth for the module dropdown
 * on the pending items dashboard.
 */
export type PendingItemsModule =
  | "NEW_APP"
  | "FOLLOW_UP"
  | "OPPOSITION"
  | "ASSIGNMENT"
  | "RENEWAL"
  | "LEGAL_CERTIFICATE"
  | "SURVEILLANCE";

export interface PendingItemsModuleOption {
  value: PendingItemsModule;
  label: string;
}

export const PENDING_ITEMS_MODULE_OPTIONS: PendingItemsModuleOption[] = [
  { value: "NEW_APP", label: "New Application" },
  { value: "FOLLOW_UP", label: "Follow Up" },
  { value: "OPPOSITION", label: "Opposition" },
  { value: "ASSIGNMENT", label: "Assignment" },
  { value: "RENEWAL", label: "Renewal" },
  { value: "LEGAL_CERTIFICATE", label: "Legal Certificate" },
  { value: "SURVEILLANCE", label: "Surveillance" },
];

export interface PendingItemsListResult {
  items: NewApplicationListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Fetches the paginated list of leads/items for the given module, normalizing to the
 * common `NewApplicationListItem` row shape so the dashboard table can render any module
 * without per-module branching. Only NEW_APP currently provides `markDisplay`; other
 * modules fall back to plain `markName` at render time.
 */
export async function listPendingItemsByModule(
  module: PendingItemsModule,
  page: number,
  limit: number,
  search?: string
): Promise<PendingItemsListResult> {
  switch (module) {
    case "NEW_APP":
      return listApplications(page, limit, search);
    case "FOLLOW_UP":
      return listFollowUps(page, limit, search) as Promise<PendingItemsListResult>;
    case "OPPOSITION":
      return listOppositionLeads({ page, limit, search }) as Promise<PendingItemsListResult>;
    case "ASSIGNMENT":
      return listAssignmentLeads({ page, limit, search }) as Promise<PendingItemsListResult>;
    case "RENEWAL":
      return listRenewalLeads({ page, limit, search }) as Promise<PendingItemsListResult>;
    case "LEGAL_CERTIFICATE":
      return listLegalCertificateLeads({ page, limit, search }) as Promise<PendingItemsListResult>;
    case "SURVEILLANCE":
      return listSurveillanceLeads({ page, limit, search }) as Promise<PendingItemsListResult>;
  }
}
